import { Button } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Upload, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { SkillSummary } from "@/api";
import { listSkillsOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { EntityListPage } from "@/components/build/EntityListPage";
import { EntityRow } from "@/components/build/EntityRow";
import { byName, byNewest, useListControls } from "@/components/build/useListControls";
import { BuildWithAiButton } from "@/features/build/withAi/BuildWithAiButton";
import { SkillIcon } from "./SkillIcon";
import { useDeleteSkill } from "./useDeleteSkill";

/** "Mine" = not a system/bundled skill (no role concept on skills). */
const isMine = (s: SkillSummary) => !s.isSystem && Boolean(s.currentUserRole);

/** Build > Skills list. Import is a secondary action in the toolbar area. */
export function SkillsListPage() {
  const { t } = useTranslation(["build", "skills"]);
  const navigate = useNavigate();
  const { onDelete, dialog } = useDeleteSkill();
  const { search, apply, listProps } = useListControls<SkillSummary>({
    isMine,
    sorts: [
      { value: "popular", label: t("build:list.sort.popular"), compare: (a, b) => b.usageCount - a.usageCount },
      { value: "name", label: t("build:list.sort.name"), compare: byName },
      { value: "newest", label: t("build:list.sort.newest"), compare: byNewest },
    ],
  });

  const query = useQuery(listSkillsOptions({ query: { search: search || undefined, page_size: 50 } }));
  const skills = apply(query.data?.data, { searched: true });

  return (
    <EntityListPage
      title={t("skills:title")}
      subtitle={t("skills:subtitle")}
      newLabel={t("skills:new")}
      onNew={() => navigate("/build/skills/new")}
      headerActions={
        <>
          <BuildWithAiButton entity="skill" />
          <Button
            variant="secondary"
            leadingIcon={<Upload size={16} />}
            onClick={() => navigate("/build/skills/import")}
          >
            {t("skills:import")}
          </Button>
        </>
      }
      {...listProps}
    >
      {skills.map((skill) => (
        <EntityRow
          key={skill.uuid}
          icon={<SkillIcon icon={skill.icon} />}
          name={skill.name}
          description={skill.description}
          onOpen={() => navigate(`/build/skills/${skill.uuid}`)}
          menuItems={[
            { label: t("build:menu.edit"), onSelect: () => navigate(`/build/skills/${skill.uuid}`) },
            ...(skill.isSystem
              ? []
              : [{ label: t("build:menu.delete"), danger: true, onSelect: () => onDelete(skill) }]),
          ]}
        />
      ))}
      {skills.length === 0 &&
        (search ? (
          <EmptyState title={t("build:list.noResultsTitle")} body={t("build:list.noResultsBody", { query: search })} />
        ) : (
          <EmptyState icon={<Zap size={32} />} title={t("skills:empty.title")} body={t("skills:empty.body")} />
        ))}
      {dialog}
    </EntityListPage>
  );
}
